import { useCallback, useMemo, useState } from 'react';
import type { JSX, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $getSelection, $isRangeSelection } from 'lexical';
import type { TextNode } from 'lexical';
import { T } from 'gt-react';
import { $createMentionNode } from './MentionNode';
import type { MentionGroup, MentionOption, MentionSource } from './types';

interface MentionPluginProps {
  sources: MentionSource[];
}

/** Flattened menu option carrying its source context and group label. */
class MentionMenuOption extends MenuOption {
  source: MentionSource;
  option: MentionOption;
  groupLabel: string;

  constructor(
    source: MentionSource,
    option: MentionOption,
    groupLabel: string,
  ) {
    super(`${source.key}:${option.id}`);
    this.source = source;
    this.option = option;
    this.groupLabel = groupLabel;
  }
}

interface GroupedOptions {
  sourceLabel: string;
  groupLabel: string;
  options: MentionMenuOption[];
}

function buildGroupedOptions(
  sources: MentionSource[],
  query: string,
): { flat: MentionMenuOption[]; grouped: GroupedOptions[] } {
  const flat: MentionMenuOption[] = [];
  const grouped: GroupedOptions[] = [];
  const groupedMap = new Map<string, GroupedOptions>();

  for (const source of sources) {
    const groups: MentionGroup[] = source.getGroups(query);
    for (const group of groups) {
      if (group.options.length === 0) continue;
      const menuOptions = group.options.map(
        (opt) => new MentionMenuOption(source, opt, group.label),
      );
      flat.push(...menuOptions);
      const mapKey = `${source.label}|${group.label}`;
      const existing = groupedMap.get(mapKey);
      if (existing) {
        existing.options.push(...menuOptions);
      } else {
        const newGroup: GroupedOptions = {
          sourceLabel: source.label,
          groupLabel: group.label,
          options: menuOptions,
        };
        grouped.push(newGroup);
        groupedMap.set(mapKey, newGroup);
      }
    }
  }

  return { flat, grouped };
}

function MentionMenuItem({
  option,
  isSelected,
  onSelect,
  onMouseEnter,
}: {
  option: MentionMenuOption;
  isSelected: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
}): JSX.Element {
  return (
    <li
      ref={(el) => option.setRefElement(el)}
      role="option"
      aria-selected={isSelected}
      className={`flex flex-col px-3 py-1.5 cursor-pointer select-none rounded ${
        isSelected
          ? 'bg-accent text-accent-foreground'
          : 'text-popover-foreground hover:bg-accent'
      }`}
      // onMouseDown + preventDefault keeps editor focus so the typeahead
      // plugin stays active and onSelectOption can complete the insertion.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onMouseEnter}
    >
      <span className="text-sm font-medium">{option.option.displayName}</span>
      {option.option.description && (
        <span className="text-xs text-muted-foreground truncate">
          {option.option.description}
        </span>
      )}
    </li>
  );
}

function MentionDropdown({
  anchorElementRef,
  selectedIndex,
  selectOptionAndCleanUp,
  setHighlightedIndex,
  options,
  grouped,
}: {
  anchorElementRef: MutableRefObject<HTMLElement | null>;
  selectedIndex: number | null;
  selectOptionAndCleanUp: (option: MentionMenuOption) => void;
  setHighlightedIndex: (index: number) => void;
  options: MentionMenuOption[];
  grouped: GroupedOptions[];
}): JSX.Element | null {
  if (!anchorElementRef.current) return null;

  let flatIndex = 0;

  return createPortal(
    <div
      className="z-50 w-64 max-h-72 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1"
      role="listbox"
    >
      {options.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          <T context="mention picker empty state">No results</T>
        </p>
      ) : (
        grouped.map((section, sectionIdx) => {
          const sectionItems = section.options.map((opt) => {
            const currentIndex = flatIndex++;
            return (
              <MentionMenuItem
                key={opt.key}
                option={opt}
                isSelected={selectedIndex === currentIndex}
                onSelect={() => selectOptionAndCleanUp(opt)}
                onMouseEnter={() => setHighlightedIndex(currentIndex)}
              />
            );
          });

          return (
            <div
              key={`${section.sourceLabel}-${section.groupLabel}-${sectionIdx}`}
            >
              {/* Source label — shown once at start of each source's first group */}
              {sectionIdx === 0 ||
              grouped[sectionIdx - 1]?.sourceLabel !== section.sourceLabel ? (
                <div className="px-3 pt-2 pb-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {section.sourceLabel}
                </div>
              ) : null}
              {/* Group label */}
              <div className="px-3 pt-1 pb-0.5 text-xs text-muted-foreground">
                {section.groupLabel}
              </div>
              <ul role="group">{sectionItems}</ul>
            </div>
          );
        })
      )}
    </div>,
    anchorElementRef.current,
  );
}

export function MentionPlugin({
  sources,
}: MentionPluginProps): JSX.Element | null {
  const [query, setQuery] = useState<string>('');

  const triggerFn = useBasicTypeaheadTriggerMatch('@', {
    minLength: 0,
    maxLength: 50,
    allowWhitespace: false,
  });

  const { flat: options, grouped } = useMemo(
    () => buildGroupedOptions(sources, query),
    [sources, query],
  );

  const onQueryChange = useCallback((matchingString: string | null) => {
    setQuery(matchingString ?? '');
  }, []);

  const onSelectOption = useCallback(
    (
      selectedOption: MentionMenuOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      const mentionNode = $createMentionNode(
        selectedOption.source.key,
        selectedOption.option.id,
      );
      if (nodeToReplace) {
        nodeToReplace.replace(mentionNode);
      } else {
        // $splitNodeContainingQuery returns null when the selection moved
        // before Lexical's update ran (e.g. on click). Fall back to inserting
        // at the current cursor position.
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([mentionNode]);
        }
      }
      closeMenu();
    },
    [],
  );

  const menuRenderFn = useCallback(
    (
      anchorElementRef: MutableRefObject<HTMLElement | null>,
      {
        selectedIndex,
        selectOptionAndCleanUp,
        setHighlightedIndex,
        options: renderedOptions,
      }: {
        selectedIndex: number | null;
        selectOptionAndCleanUp: (option: MentionMenuOption) => void;
        setHighlightedIndex: (index: number) => void;
        options: MentionMenuOption[];
      },
    ) => {
      return (
        <MentionDropdown
          anchorElementRef={anchorElementRef}
          selectedIndex={selectedIndex}
          selectOptionAndCleanUp={selectOptionAndCleanUp}
          setHighlightedIndex={setHighlightedIndex}
          options={renderedOptions}
          grouped={grouped}
        />
      );
    },
    [grouped],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionMenuOption>
      options={options}
      onQueryChange={onQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      menuRenderFn={menuRenderFn}
    />
  );
}
